<template>
  <div class="support-dispatch">
    <div class="section-header">
      <h2 class="section-title">Dispatch Support</h2>
      <p class="section-subtitle">Créer une mission au nom d'un client suite à un appel entrant</p>
    </div>

    <!-- Step indicator -->
    <div class="steps-indicator">
      <div
        v-for="(step, i) in steps"
        :key="i"
        class="step"
        :class="{ active: currentStep === i, done: currentStep > i }"
      >
        <div class="step-circle">
          <el-icon v-if="currentStep > i"><Check /></el-icon>
          <span v-else>{{ i + 1 }}</span>
        </div>
        <span class="step-label">{{ step }}</span>
      </div>
      <div class="step-line" />
    </div>

    <!-- Step 1: Find client -->
    <div v-if="currentStep === 0" class="card dispatch-card">
      <h3 class="card-title">Rechercher le client</h3>
      <div class="form-row">
        <el-input
          v-model="phoneQuery"
          placeholder="Numéro de téléphone (min. 4 chiffres)"
          clearable
          @keyup.enter="searchClients"
        >
          <template #prefix><el-icon><PhoneFilled /></el-icon></template>
        </el-input>
        <el-button type="primary" :loading="searching" @click="searchClients">Rechercher</el-button>
      </div>

      <div v-if="clientResults.length > 0" class="results-list">
        <div
          v-for="client in clientResults"
          :key="client.id"
          class="result-item"
          :class="{ selected: selectedClient?.id === client.id, inactive: !client.is_active }"
          @click="client.is_active && selectClient(client)"
        >
          <div class="result-avatar">{{ (client.full_name || client.phone).charAt(0).toUpperCase() }}</div>
          <div class="result-info">
            <div class="result-name">{{ client.full_name || '—' }}</div>
            <div class="result-sub">{{ client.phone }}</div>
          </div>
          <el-tag v-if="!client.is_active" type="danger" size="small">Inactif</el-tag>
          <el-tag v-else-if="selectedClient?.id === client.id" type="success" size="small">Sélectionné</el-tag>
        </div>
      </div>
      <div v-else-if="searched && clientResults.length === 0" class="empty-state">
        Aucun client trouvé pour "{{ phoneQuery }}"
      </div>

      <div class="card-footer">
        <el-button type="primary" :disabled="!selectedClient" @click="currentStep = 1">
          Continuer →
        </el-button>
      </div>
    </div>

    <!-- Step 2: Problem description -->
    <div v-if="currentStep === 1" class="card dispatch-card">
      <h3 class="card-title">Décrire le problème</h3>

      <div class="selected-client-banner">
        <el-icon><User /></el-icon>
        <span>Client : <strong>{{ selectedClient.full_name || selectedClient.phone }}</strong></span>
      </div>

      <div class="form-group">
        <label>Catégorie de service</label>
        <el-select v-model="form.category_id" placeholder="Sélectionner une catégorie" style="width:100%">
          <el-option
            v-for="cat in categories"
            :key="cat.id"
            :label="cat.name"
            :value="cat.id"
          />
        </el-select>
      </div>

      <div class="form-group">
        <label>Description du problème</label>
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          placeholder="Ex : Climatiseur ne refroidit plus, panne totale depuis ce matin"
        />
      </div>

      <div class="form-group">
        <label>Repère / Adresse (optionnel)</label>
        <el-input
          v-model="form.landmark_text"
          placeholder="Ex : Derrière la station ORYX, Secteur 15"
        />
      </div>

      <div class="card-footer">
        <el-button @click="currentStep = 0">← Retour</el-button>
        <el-button
          type="primary"
          :disabled="!form.category_id || !form.description.trim()"
          @click="loadArtisans"
        >
          Continuer →
        </el-button>
      </div>
    </div>

    <!-- Step 3: Select artisan -->
    <div v-if="currentStep === 2" class="card dispatch-card">
      <h3 class="card-title">Choisir un artisan disponible</h3>

      <div class="selected-client-banner">
        <el-icon><User /></el-icon>
        <span>
          {{ selectedClient.full_name || selectedClient.phone }} —
          <strong>{{ categoryName }}</strong> —
          {{ form.description.slice(0, 60) }}{{ form.description.length > 60 ? '…' : '' }}
        </span>
      </div>

      <div v-if="loadingArtisans" class="loading-row">
        <el-icon class="is-loading"><Loading /></el-icon> Chargement des artisans…
      </div>
      <div v-else-if="artisans.length === 0" class="empty-state">
        Aucun artisan approuvé disponible pour cette catégorie.
      </div>
      <div v-else class="artisan-grid">
        <div
          v-for="artisan in artisans"
          :key="artisan.id"
          class="artisan-card"
          :class="{ selected: selectedArtisan?.id === artisan.id, online: artisan.is_online }"
          @click="selectedArtisan = artisan"
        >
          <div class="artisan-avatar">{{ (artisan.full_name || artisan.phone || '?').charAt(0).toUpperCase() }}</div>
          <div class="artisan-info">
            <div class="artisan-name">{{ artisan.full_name || artisan.phone }}</div>
            <div class="artisan-meta">
              <span>⭐ {{ artisan.rating_avg?.toFixed(1) || 'N/A' }}</span>
              <span v-if="artisan.quartier">· {{ artisan.quartier }}</span>
              <span v-if="artisan.has_motorcycle" class="inline-flex items-center gap-1">
                · <i class="mdi mdi-motorbike text-profissa-text-muted" aria-hidden="true"></i>
              </span>
            </div>
            <div class="artisan-price">{{ artisan.diagnostic_price?.toLocaleString() }} FCFA diag.</div>
          </div>
          <div class="artisan-status" :class="{ online: artisan.is_online }">
            {{ artisan.is_online ? 'En ligne' : 'Hors ligne' }}
          </div>
        </div>
      </div>

      <div class="card-footer">
        <el-button @click="currentStep = 1">← Retour</el-button>
        <el-button
          type="primary"
          :disabled="!selectedArtisan"
          :loading="dispatching"
          @click="confirmDispatch"
        >
          Créer la mission
        </el-button>
      </div>
    </div>

    <!-- Success -->
    <div v-if="currentStep === 3" class="card dispatch-card success-card">
      <div class="success-icon"><el-icon><CircleCheckFilled /></el-icon></div>
      <h3>Mission créée avec succès</h3>
      <div class="success-details">
        <div><span>Référence</span><strong>{{ result.request_display_id }}</strong></div>
        <div><span>Client</span><strong>{{ result.client_phone }}</strong></div>
        <div><span>Artisan</span><strong>{{ result.artisan_phone }}</strong></div>
      </div>
      <p class="success-note">Une notification push a été envoyée au client et à l'artisan.</p>
      <el-button type="primary" @click="reset">Nouveau dispatch</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { PhoneFilled, User, Loading, Check, CircleCheckFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { supportSearchUsers, supportAvailableArtisans, supportDispatch, listCategories } from '../services/adminApi'

const steps = ['Client', 'Problème', 'Artisan']
const currentStep = ref(0)

const phoneQuery    = ref('')
const clientResults = ref([])
const searching     = ref(false)
const searched      = ref(false)
const selectedClient = ref(null)

const categories     = ref([])
const form           = ref({ category_id: '', description: '', landmark_text: '' })

const artisans          = ref([])
const loadingArtisans   = ref(false)
const selectedArtisan   = ref(null)

const dispatching = ref(false)
const result      = ref(null)

const categoryName = computed(() =>
  categories.value.find(c => c.id === form.value.category_id)?.name || ''
)

onMounted(async () => {
  try {
    categories.value = await listCategories()
  } catch { /* non-blocking */ }
})

const searchClients = async () => {
  if (phoneQuery.value.length < 4) {
    ElMessage.warning('Saisissez au moins 4 chiffres.')
    return
  }
  searching.value = true
  searched.value = false
  try {
    clientResults.value = await supportSearchUsers(phoneQuery.value)
    searched.value = true
  } catch (e) {
    ElMessage.error('Erreur lors de la recherche.')
  } finally {
    searching.value = false
  }
}

const selectClient = (client) => {
  selectedClient.value = client
}

const loadArtisans = async () => {
  currentStep.value = 2
  loadingArtisans.value = true
  selectedArtisan.value = null
  try {
    artisans.value = await supportAvailableArtisans(form.value.category_id)
  } catch {
    ElMessage.error('Erreur lors du chargement des artisans.')
  } finally {
    loadingArtisans.value = false
  }
}

const confirmDispatch = async () => {
  dispatching.value = true
  try {
    result.value = await supportDispatch({
      client_id:     selectedClient.value.id,
      artisan_id:    selectedArtisan.value.id,
      category_id:   form.value.category_id,
      description:   form.value.description,
      landmark_text: form.value.landmark_text || null,
    })
    currentStep.value = 3
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || 'Erreur lors du dispatch.')
  } finally {
    dispatching.value = false
  }
}

const reset = () => {
  currentStep.value = 0
  phoneQuery.value = ''
  clientResults.value = []
  searched.value = false
  selectedClient.value = null
  form.value = { category_id: '', description: '', landmark_text: '' }
  artisans.value = []
  selectedArtisan.value = null
  result.value = null
}
</script>

<style scoped>
.support-dispatch { max-width: 760px; margin: 0 auto; }

.steps-indicator {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 32px;
  position: relative;
}
.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  position: relative;
  z-index: 1;
}
.step-circle {
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 2px solid var(--glass-border);
  background: var(--bg-card);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 700;
  color: var(--text-muted);
  transition: all 0.2s;
}
.step.active .step-circle { border-color: var(--profissa-accent); color: var(--profissa-accent); }
.step.done .step-circle { background: var(--profissa-accent); border-color: var(--profissa-accent); color: #fff; }
.step-label { font-size: 0.75rem; color: var(--text-muted); }
.step.active .step-label { color: var(--profissa-accent); }
.step-line {
  position: absolute;
  top: 16px; left: 16%; right: 16%;
  height: 2px;
  background: var(--glass-border);
  z-index: 0;
}

.dispatch-card { padding: 28px; margin-bottom: 24px; }
.card-title { font-size: 1.1rem; font-weight: 700; margin-bottom: 20px; }

.form-row { display: flex; gap: 10px; margin-bottom: 16px; }
.form-row .el-input { flex: 1; }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 0.8rem; color: var(--text-muted); margin-bottom: 6px; }

.results-list { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
.result-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; border-radius: 10px;
  border: 1px solid var(--glass-border);
  background: var(--bg-card);
  cursor: pointer; transition: all 0.15s;
}
.result-item:hover:not(.inactive) { border-color: var(--profissa-accent); }
.result-item.selected { border-color: var(--profissa-accent); background: rgba(255,102,0,0.08); }
.result-item.inactive { opacity: 0.5; cursor: not-allowed; }
.result-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--profissa-accent); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.9rem;
}
.result-name { font-weight: 600; font-size: 0.9rem; }
.result-sub { font-size: 0.8rem; color: var(--text-muted); }

.selected-client-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 8px;
  background: rgba(255,102,0,0.08);
  border: 1px solid rgba(255,102,0,0.2);
  font-size: 0.85rem; color: var(--text-muted);
  margin-bottom: 20px;
}

.artisan-grid { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.artisan-card {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px; border-radius: 10px;
  border: 1px solid var(--glass-border);
  background: var(--bg-card);
  cursor: pointer; transition: all 0.15s;
}
.artisan-card:hover { border-color: var(--profissa-accent); }
.artisan-card.selected { border-color: var(--profissa-accent); background: rgba(255,102,0,0.08); }
.artisan-avatar {
  width: 42px; height: 42px; border-radius: 50%;
  background: var(--profissa-blue, #1e40af); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700;
}
.artisan-info { flex: 1; }
.artisan-name { font-weight: 600; }
.artisan-meta { font-size: 0.8rem; color: var(--text-muted); margin-top: 2px; }
.artisan-price { font-size: 0.8rem; color: var(--profissa-accent); margin-top: 2px; }
.artisan-status {
  font-size: 0.75rem; padding: 3px 10px; border-radius: 20px;
  background: var(--glass-bg); color: var(--text-muted);
}
.artisan-status.online { background: rgba(34,197,94,0.15); color: #22c55e; }

.card-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 24px; padding-top: 16px;
  border-top: 1px solid var(--glass-border);
}

.empty-state { text-align: center; color: var(--text-muted); padding: 24px; font-size: 0.9rem; }
.loading-row { display: flex; align-items: center; gap: 8px; color: var(--text-muted); padding: 16px; }

.success-card { text-align: center; padding: 40px 28px; }
.success-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: rgba(34,197,94,0.15); color: #22c55e;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.8rem; margin: 0 auto 16px;
}
.success-card h3 { font-size: 1.2rem; margin-bottom: 20px; }
.success-details { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.success-details div {
  display: flex; justify-content: space-between;
  padding: 8px 16px; border-radius: 8px;
  background: var(--bg-card); font-size: 0.9rem;
}
.success-details span { color: var(--text-muted); }
.success-note { font-size: 0.8rem; color: var(--text-muted); margin-bottom: 24px; }
</style>
